import { useState } from "react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { SrIcon } from "@/components/ui/SrIcon";
import { cn } from "@/lib/utils";
import type { OfferLineTemplate } from "@/hooks/use-offers";

interface Props {
  templates: OfferLineTemplate[];
  currentId: string | null;
  currentName: string | null;
  onSelect: (template: OfferLineTemplate | null) => void;
  /** Visuell variant: "tag" = liten tag-pill (Tilbud-tab/Import-dialog),
   *  "compact" = enda mindre for inline-bruk */
  variant?: "tag" | "compact";
}

/**
 * VIB-586: Søkbar mal-velger. Viser nåværende mal som klikkbar tag, klikk
 * åpner Command-popup over alle aktive offer_line_templates (dedupert på
 * navn). Brukes både i ImportBudgetDialog (preview) og OfferLineEditRow
 * (Tilbud-tab redigering).
 *
 * onSelect(null) = fjern mal-kobling.
 */
export function TemplatePicker({
  templates,
  currentId,
  currentName,
  onSelect,
  variant = "tag",
}: Props) {
  const [open, setOpen] = useState(false);
  const matched = currentId !== null;

  const seen = new Set<string>();
  const dedupedTemplates = templates
    .filter((t) => t.is_active)
    .filter((t) => {
      const key = t.name.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "inline-flex items-center gap-1 truncate max-w-full hover:underline",
            variant === "tag" ? "mt-0.5 text-[10px]" : "text-[11px]",
            matched ? "text-success" : "text-yellow-700",
          )}
          title={matched ? "Klikk for å bytte mal" : "Klikk for å velge mal"}
        >
          {!matched && (
            <span
              className="inline-flex w-2 h-2 rounded-full bg-yellow-500 shrink-0"
              aria-hidden
            />
          )}
          <span className="truncate">
            ↳ {matched ? currentName : "Velg mal..."}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-72" align="start">
        <Command>
          <CommandInput placeholder="Søk i maler..." className="h-9" />
          <CommandList className="max-h-72">
            <CommandEmpty>Ingen maler matcher søket.</CommandEmpty>
            {matched && (
              <CommandGroup heading="Aktiv mal">
                <CommandItem
                  value="__clear__"
                  onSelect={() => {
                    onSelect(null);
                    setOpen(false);
                  }}
                  className="text-xs text-muted-foreground"
                >
                  <SrIcon name="x" className="w-3 h-3 mr-1.5" />
                  Fjern mal-kobling
                </CommandItem>
              </CommandGroup>
            )}
            <CommandGroup heading={`${dedupedTemplates.length} maler`}>
              {dedupedTemplates.map((t) => (
                <CommandItem
                  key={t.id}
                  value={`${t.name} ${t.phase}`}
                  onSelect={() => {
                    onSelect(t);
                    setOpen(false);
                  }}
                >
                  <div className="flex items-center justify-between w-full">
                    <span className="text-xs truncate">{t.name}</span>
                    <span className="text-[10px] text-muted-foreground ml-2 shrink-0">
                      {t.phase}
                    </span>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
